/**
 * Hotel Types for Voyage-AI
 * Comprehensive TypeScript definitions for hotel search and booking
 */

// ============================================================================
// Core Hotel Types
// ============================================================================

/**
 * Hotel search parameters
 */
export interface HotelSearchParams {
  destination: {
    city: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  checkIn: string;
  checkOut: string;
  rooms: number;
  guests: {
    adults: number;
    children: number;
    childrenAges?: number[];
  };
  filters?: HotelFilters;
  sortBy?: HotelSortOption;
  maxResults?: number;
}

/**
 * Individual hotel from search results
 */
export interface Hotel {
  id: string;
  name: string;
  description?: string;
  
  // Location
  location: HotelLocation;
  address: HotelAddress;
  coordinates: {
    lat: number;
    lng: number;
  };
  
  // Rating and classification
  rating: HotelRating;
  starRating: StarRating;
  reviews: HotelReviews;
  
  // Media
  images: HotelImage[];
  primaryImage?: string;
  
  // Pricing
  price: HotelPrice;
  originalPrice?: number;
  discount?: number;
  
  // Room availability
  rooms: RoomType[];
  availableRooms: number;
  
  // Amenities and features
  amenities: HotelAmenity[];
  policies: HotelPolicies;
  
  // Contact
  contact: HotelContact;
  
  // Metadata
  provider: string;
  lastUpdated: string;
}

/**
 * Hotel location information
 */
export interface HotelLocation {
  city: string;
  state?: string;
  country: string;
  neighborhood?: string;
  area?: string;
  distanceFromCenter?: number;  // in km
  distanceFromPointOfInterest?: number;
}

/**
 * Hotel address
 */
export interface HotelAddress {
  street: string;
  number?: string;
  postalCode?: string;
  city: string;
  state?: string;
  country: string;
  formattedAddress?: string;
}

/**
 * Hotel rating information
 */
export interface HotelRating {
  overall: number;  // 0-5 scale
  cleanliness: number;
  comfort: number;
  location: number;
  facilities: number;
  staff: number;
  value: number;
  maxRating: number;
}

/**
 * Star rating (official hotel classification)
 */
export type StarRating = 1 | 2 | 3 | 4 | 5;

/**
 * Hotel reviews summary
 */
export interface HotelReviews {
  totalCount: number;
  averageRating: number;
  breakdown: {
    excellent: number;
    veryGood: number;
    average: number;
    poor: number;
    terrible: number;
  };
  recentReviews?: Review[];
}

/**
 * Individual review
 */
export interface Review {
  id: string;
  author: string;
  rating: number;
  title: string;
  content: string;
  date: string;
  pros?: string[];
  cons?: string[];
  response?: string;
  helpful?: number;
}

/**
 * Hotel image
 */
export interface HotelImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  caption?: string;
  category: 'exterior' | 'lobby' | 'room' | 'bathroom' | 'restaurant' | 'pool' | 'gym' | 'spa' | 'view' | 'other';
  is360?: boolean;
}

/**
 * Price breakdown for hotel
 */
export interface HotelPrice {
  perNight: number;
  total: number;
  currency: string;
  taxes: number;
  fees: number;
  breakdown?: {
    baseRate: number;
    taxes: number;
    resortFees: number;
    serviceFees: number;
  };
  pricePerNightAdults?: number;
  pricePerNightChildren?: number;
  averageNightlyRate?: number;
}

/**
 * Room type within a hotel
 */
export interface RoomType {
  id: string;
  name: string;
  description?: string;
  maxOccupancy: number;
  maxAdults: number;
  maxChildren: number;
  bedType: BedType;
  bedCount: number;
  bedDetails?: BedDetail[];
  size?: {
    value: number;
    unit: 'sqft' | 'sqm';
  };
  
  // Pricing
  price: HotelPrice;
  available: boolean;
  availableCount: number;
  
  // Amenities
  amenities: string[];
  
  // Images
  images: HotelImage[];
  
  // Cancellation policy
  cancellationPolicy?: string;
  cancellationDeadline?: string;
  prepayment?: boolean;
}

/**
 * Bed type options
 */
export type BedType = 
  | 'single'
  | 'double'
  | 'queen'
  | 'king'
  | 'twin'
  | 'full'
  | 'double-double'
  | 'suite';

/**
 * Individual bed details
 */
export interface BedDetail {
  type: BedType;
  count: number;
}

/**
 * Hotel amenities
 */
export interface HotelAmenity {
  id: string;
  name: string;
  category: AmenityCategory;
  icon?: string;
  description?: string;
  isHighlight?: boolean;
}

/**
 * Amenity categories
 */
export type AmenityCategory = 
  | 'room'
  | 'bathroom'
  | 'view'
  | 'food'
  | 'wellness'
  | 'activities'
  | 'business'
  | 'accessibility'
  | 'parking'
  | 'transport'
  | 'general';

/**
 * Common hotel amenities (for easier filtering)
 */
export enum HotelAmenityType {
  WIFI = 'wifi',
  PARKING = 'parking',
  POOL = 'pool',
  GYM = 'gym',
  SPA = 'spa',
  RESTAURANT = 'restaurant',
  BAR = 'bar',
  ROOM_SERVICE = 'room_service',
  CONCIERGE = 'concierge',
  LAUNDRY = 'laundry',
  BUSINESS_CENTER = 'business_center',
  MEETING_ROOMS = 'meeting_rooms',
  AIRPORT_SHUTTLE = 'airport_shuttle',
  PET_FRIENDLY = 'pet_friendly',
  NON_SMOKING = 'non_smoking',
  AIR_CONDITIONING = 'air_conditioning',
  HEATING = 'heating',
  BALCONY = 'balcony',
  OCEAN_VIEW = 'ocean_view',
  KITCHEN = 'kitchen',
}

/**
 * Hotel policies
 */
export interface HotelPolicies {
  checkIn: {
    time: string;
    earliest?: string;
    latest?: string;
  };
  checkOut: {
    time: string;
    latest?: string;
  };
  children: ChildrenPolicy;
  pets: PetPolicy;
  cancellation: CancellationPolicy;
  payment: PaymentPolicy;
  additionalPolicies?: string[];
}

/**
 * Children policy
 */
export interface ChildrenPolicy {
  allowed: boolean;
  maxAge?: number;
  extraBedAllowed?: boolean;
  extraBedCharge?: number;
  cribAvailable?: boolean;
  cribCharge?: number;
}

/**
 * Pet policy
 */
export interface PetPolicy {
  allowed: boolean;
  allowedTypes?: string[];
  maxWeight?: number;
  charge?: number;
  restrictions?: string[];
}

/**
 * Cancellation policy
 */
export interface CancellationPolicy {
  freeCancellation: boolean;
  deadline?: string;
  cancellationCharge?: number;
  description?: string;
  tiers?: CancellationTier[];
}

/**
 * Tiered cancellation policy
 */
export interface CancellationTier {
  deadline: string;
  charge: number;
  chargeType: 'percentage' | 'fixed';
}

/**
 * Payment policy
 */
export interface PaymentPolicy {
  acceptedPaymentMethods: string[];
  depositRequired: boolean;
  depositPercentage?: number;
  prepaymentRequired: boolean;
}

/**
 * Hotel contact information
 */
export interface HotelContact {
  phone: string;
  email?: string;
  website?: string;
  emergencyPhone?: string;
}

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Available filters for hotel search results
 */
export interface HotelFilters {
  priceRange: {
    min: number;
    max: number;
  };
  starRating: StarRating[];
  amenities: string[];
  guestRating: {
    min: number;
  };
  distance: {
    maxFromCenter: number;
    maxFromPointOfInterest?: number;
  };
  propertyType: PropertyType[];
  neighborhood?: string[];
  freeCancellation?: boolean;
  bookingOptions: {
    payLater: boolean;
    freeBreakfast: boolean;
  };
  accessibility: {
    wheelchairAccessible: boolean;
    accessibleParking: boolean;
  };
}

/**
 * Hotel property types
 */
export type PropertyType = 
  | 'hotel'
  | 'resort'
  | 'boutique'
  | 'motel'
  | 'hostel'
  | 'aparthotel'
  | 'villa'
  | 'cottage'
  | 'b&b'
  | 'inn';

/**
 * Sort options for hotel results
 */
export type HotelSortOption = 
  | 'price-asc'
  | 'price-desc'
  | 'rating-asc'
  | 'rating-desc'
  | 'stars-asc'
  | 'stars-desc'
  | 'distance-asc'
  | 'reviews-asc'
  | 'reviews-desc';

// ============================================================================
// Selection and Booking Types
// ============================================================================

/**
 * Selected hotel for itinerary
 */
export interface SelectedHotel {
  hotel: Hotel;
  room: RoomType;
  checkIn: string;
  checkOut: string;
  guests: {
    adults: number;
    children: number;
  };
  totalPrice: number;
  currency: string;
  specialRequests?: string[];
}

/**
 * Hotel reservation/booking
 */
export interface HotelReservation {
  id: string;
  hotelId: string;
  roomId: string;
  checkIn: string;
  checkOut: string;
  guests: {
    name: string;
    email: string;
    phone: string;
  };
  status: ReservationStatus;
  price: HotelPrice;
  confirmationNumber?: string;
  bookingDate: string;
  paymentStatus: PaymentStatus;
  specialRequests?: string;
}

/**
 * Reservation status
 */
export type ReservationStatus = 
  | 'pending'
  | 'confirmed'
  | 'cancelled'
  | 'completed'
  | 'no-show';

/**
 * Payment status
 */
export type PaymentStatus = 
  | 'pending'
  | 'authorized'
  | 'captured'
  | 'refunded'
  | 'failed';

// ============================================================================
// API Response Types
// ============================================================================

/**
 * API response from hotel search
 */
export interface HotelSearchResponse {
  hotels: Hotel[];
  count: number;
  searchParams: HotelSearchParams;
  
  // Pagination
  page: number;
  pageSize: number;
  totalResults: number;
  hasMore: boolean;
  
  // Metadata
  searchId?: string;
  cachedAt?: string;
  
  // Alternative suggestions
  alternativeDestinations?: DestinationSuggestion[];
  
  // Errors/warnings
  warnings?: string[];
}

/**
 * Destination suggestion for autocomplete
 */
export interface DestinationSuggestion {
  city: string;
  country: string;
  region?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  hotelCount?: number;
  avgPrice?: number;
  type: 'city' | 'region' | 'hotel' | 'landmark';
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Hotel search form data (what user enters)
 */
export interface HotelSearchFormData {
  destination: {
    city: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  checkIn: string;
  checkOut: string;
  rooms: number;
  guests: {
    adults: number;
    children: number;
  };
  flexibleDates?: boolean;
}

/**
 * Convert search form data to search params
 */
export function hotelSearchFormDataToParams(
  formData: HotelSearchFormData
): HotelSearchParams {
  const nights = calculateNights(formData.checkIn, formData.checkOut);
  
  return {
    destination: formData.destination,
    checkIn: formData.checkIn,
    checkOut: formData.checkOut,
    rooms: formData.rooms,
    guests: formData.guests,
  };
}

/**
 * Calculate number of nights between dates
 */
export function calculateNights(checkIn: string, checkOut: string): number {
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format price for display
 */
export function formatHotelPrice(price: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format star rating for display
 */
export function formatStarRating(rating: StarRating): string {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

/**
 * Get amenity icon name
 */
export function getAmenityIcon(amenityName: string): string {
  const amenityIcons: Record<string, string> = {
    'Wi-Fi': 'wifi',
    'Parking': 'parking',
    'Pool': 'pool',
    'Gym': 'fitness_center',
    'Spa': 'spa',
    'Restaurant': 'restaurant',
    'Bar': 'local_bar',
    'Room Service': 'room_service',
    'Air Conditioning': 'ac_unit',
    'Pet Friendly': 'pets',
    'Business Center': 'business_center',
    'Airport Shuttle': 'airport_shuttle',
    'Laundry': 'local_laundry_service',
  };
  
  return amenityIcons[amenityName] || 'check_circle';
}
